import React, { useState, useEffect, Fragment } from 'react';
import { useHistory } from 'react-router-dom';

import moment from 'moment';

import Header from 'components/AppBar';
import Loader from 'components/Loader';
import { Mobile, Desktop } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { useThereIsTravels } from 'hooks/useThereIsTravels';
import { AdminUser } from 'models/adminUser';
import { CostCenterReportRequest, CostCenterReportResponse } from 'models/costCenterReport';
import { Group } from 'models/group';
import { TravelReportRequest as Filters, TravelReportRequest, ReportSummary } from 'models/reportSummary';
import { AccountResponse } from 'models/user';
import { RenderTravelReportViewCostCenter } from 'screens/components/CostCenterReport/RenderTravelReportView';
import ReportForm from 'screens/components/TravelReport/TravelReportFilter';
import { getFormattedPrice } from 'utils/currency';
import { statusTranslation } from 'utils/statusTag';

import { EmptyTravelsView } from './EmptyTravelsView';
import { Container, Divider } from './styles';
import Summary from './TravelReportResult/components/Summary';

interface Props {
  groupList: Group[];
  accountResult: AccountResponse | null;
  adminUserList: AdminUser[];
  fetchUsersAndInvites: (companyId: string) => Promise<void>;
  fetchGroupList: (companyId: string) => Promise<void>;
  fetchReportSummary: (companyId?: string, filters?: Filters) => Promise<void>;
  fetchCostCenterReport: (costCenterFilters: CostCenterReportRequest) => Promise<void>;
  filters?: TravelReportRequest;
  travelReport?: ReportSummary;
  isLoading: boolean;
  isLoadingCostCenterReport: boolean;
  costCenterReport?: CostCenterReportResponse;
}

const TravelReport = ({
  accountResult,
  adminUserList,
  groupList,
  fetchGroupList,
  fetchUsersAndInvites,
  fetchReportSummary,
  fetchCostCenterReport,
  filters,
  travelReport,
  isLoading,
  isLoadingCostCenterReport,
  costCenterReport,
}: Props) => {
  const [periodText, setPeriodText] = useState('');

  const isTravelsData = useThereIsTravels(accountResult?.companyId);

  const { t } = useStoreTranslation();
  const history = useHistory();
  const isMobile = useIsMobile();

  const handlePeriodText = (text?: string) => {
    if (text) setPeriodText(text);
  };

  const [fetchingTravelsData, setFetchingTravelsData] = useState({ isTravels: false, isLoading: false });

  const navigateToDashboard = () => history.push('/dashboard');

  const formattedPrice = (price?: number, currency?: string): string => {
    const { formatted } = getFormattedPrice({ value: price || 0, currency: currency, t });
    return formatted;
  };

  const formatDate = (date: string) => moment(date).format('DD/MM/YYYY');

  const formatStatus = (status: string) => statusTranslation(status, t);

  useEffect(() => {
    if (fetchingTravelsData.isTravels) {
      return;
    }

    if (isTravelsData.isTravels) {
      setFetchingTravelsData({
        isTravels: isTravelsData.isTravels,
        isLoading: isTravelsData.isLoading,
      });
    }
  }, [isTravelsData]); // eslint-disable-line react-hooks/exhaustive-deps
  // TODO: check dependencies or refactor

  const RenderTravelReportView = () =>
    fetchingTravelsData.isTravels ? (
      <Container>
        <Desktop>
          <ScreenHeader title={t('userMenu.reportsTitle')} subtitle={t('userMenu.reportsDetail')} />
        </Desktop>

        <ReportForm
          loading={isLoading}
          groupList={groupList}
          accountResult={accountResult}
          adminUserList={adminUserList?.filter((user) => !user.isInvite)}
          fetchGroupList={fetchGroupList}
          fetchUsersAndInvites={fetchUsersAndInvites}
          fetchReportSummary={fetchReportSummary}
          fetchCostCenterReport={fetchCostCenterReport}
          filters={filters}
          onSetPeriodText={handlePeriodText}
        />

        <Divider />

        <Summary filters={filters} isLoading={isLoading} travelReport={travelReport} dateSelected={periodText} />

        {!isLoadingCostCenterReport && costCenterReport && !isMobile && (
          <Fragment>
            <Divider />
            <br />
          </Fragment>
        )}

        {RenderTravelReportViewCostCenter(
          false,
          [],
          isLoadingCostCenterReport,
          false,
          isMobile,
          t,
          fetchCostCenterReport,
          handlePeriodText,
          formattedPrice,
          formatDate,
          formatStatus,
          costCenterReport,
        )}
      </Container>
    ) : (
      <EmptyTravelsView />
    );

  return (
    <Fragment>
      {isLoading || isLoadingCostCenterReport ? (
        <Loader fullScreen />
      ) : (
        <Fragment>
          <Mobile>
            <Header goBack={navigateToDashboard}>{t('travelReport.headerTitle')}</Header>
          </Mobile>
          {RenderTravelReportView()}
        </Fragment>
      )}
    </Fragment>
  );
};

export default TravelReport;
